/**
 * How a numeric *reading* — the raw JSON a document analyser stored for a fact —
 * turns into a number.
 *
 * It lives in the contract, beside the dossier schemas, because two sides that
 * cannot see each other have to agree on it: the backend's `apply-field-registry`
 * coerces a reading before promoting it to an Objekt field, and the operator
 * Dossier renders the very same reading on screen. When they disagree, the screen
 * shows a number the platform never stored — a German `450.000 €` reading as
 * „450 €" is the shape of it, and the Dossier is the one surface whose entire job
 * is to show what was actually read (IMM-686).
 *
 * That was the state before this module: the rule existed once, in the backend,
 * and the renderer re-derived it with a bare `Number()`. Declared once, imported
 * by both, per BL-022 — a value shown to a user is tied to the constant that
 * enforces it, never re-typed.
 */

/**
 * German readings write `1.234,56`; a bare `1.234` is far more likely a grouped
 * thousand than one-point-two-three-four, so only an exact 3-digit grouping is
 * treated as separators. Anything else keeps the dot as a decimal point.
 */
export function normalizeNumericSeparators(value: string): string {
  const hasComma = value.includes(',');
  const hasDot = value.includes('.');

  if (hasComma && hasDot) return value.replace(/\./g, '').replace(',', '.');

  if (hasComma) return value.replace(',', '.');

  if (hasDot && /^-?\d{1,3}(\.\d{3})+$/.test(value)) return value.replace(/\./g, '');

  return value;
}

/**
 * The number a reading carries, or `null` when it carries none — an empty
 * string, a word, an infinity. `null` is what a caller must render as-stored
 * rather than dress up: `Number('')` is `0`, and a zero the extractor never read
 * is worse on a provenance screen than the raw text.
 */
export function parseNumericReading(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

  if (typeof raw !== 'string') return null;

  // Drop the units a reading carries ("120 m²", "1.200 €") before parsing.
  const cleaned = raw.replace(/[^\d,.-]/g, '');

  if (cleaned === '') return null;

  const parsed = Number(normalizeNumericSeparators(cleaned));

  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The number a reading carries **only when the reading is nothing but a number**
 * — the display counterpart of `parseNumericReading`, and deliberately stricter.
 *
 * The difference is the unit-stripping. Coercion wants a number out of „120 m²"
 * and is right to reach for one: it is deciding what to store. A renderer is not
 * — it is showing what was stored — and the same stripping turns „ca. 120" into
 * `.120` and prints „0,12 m²", inventing a precision the document never had.
 * So anything that is not purely numeric returns `null` here, and the caller
 * shows the reading as it stands.
 *
 * The separator rule is shared with coercion, which is the point: a German
 * `450.000` must not read as 450 on the one screen whose job is to show what was
 * actually extracted (IMM-686).
 */
export function parseDisplayNumericReading(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();

  if (!/^-?[\d.,]+$/.test(trimmed)) return null;

  const parsed = Number(normalizeNumericSeparators(trimmed));

  return Number.isFinite(parsed) ? parsed : null;
}
